import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button, FormControlLabel, InputAdornment, Stack, Switch, TextField } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import PremiumModal from 'components/shared/modals/PremiumModal';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { CREATE_ORGANIZATION, UPDATE_ORGANIZATION } from 'api/graphql/organization';

/**
 * Diálogo para crear/editar una organización. El código de vinculación se autogenera en el
 * backend al crear y se gestiona desde el detalle.
 */
function OrganizationForm({ open, organization = null, onClose }) {
  const { t } = useTranslation(['organizations', 'buttons']);
  const isEdit = Boolean(organization);

  const [name, setName] = useState(organization?.name ?? '');
  const [contactName, setContactName] = useState(organization?.contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(organization?.contact_email ?? '');
  const [isActive, setIsActive] = useState(organization ? organization.is_active : true);
  const [covered, setCovered] = useState(String(organization?.company_covered_percent ?? 0));
  const [notes, setNotes] = useState(organization?.notes ?? '');
  const [benefits, setBenefits] = useState(organization?.benefits ?? '');
  const [error, setError] = useState('');

  const [createOrganization, { loading: creating }] = useMutation(CREATE_ORGANIZATION);
  const [updateOrganization, { loading: updating }] = useMutation(UPDATE_ORGANIZATION);
  const saving = creating || updating;

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('nameRequired', { defaultValue: 'El nombre es obligatorio.' }));
      return;
    }
    setError('');
    const input = {
      name: name.trim(),
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      is_active: isActive,
      company_covered_percent: Math.max(0, Math.min(100, parseInt(covered, 10) || 0)),
      notes: notes.trim() || null,
      benefits: benefits.trim() || null
    };
    try {
      if (isEdit) {
        await updateOrganization({ variables: { id: organization.id, input } });
      } else {
        await createOrganization({ variables: { input } });
      }
      onClose();
    } catch (e) {
      setError(e.message || t('saveError', { defaultValue: 'No se pudo guardar.' }));
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      icon={<BusinessIcon />}
      title={isEdit ? t('editOrganization', { defaultValue: 'Editar organización' }) : t('newOrganization', { defaultValue: 'Nueva organización' })}
      actions={(
        <>
          <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
            {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
          </Button>
          <Button onClick={handleSave} disabled={saving} sx={{ ...premiumButtonSx, px: 3 }}>
            {t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
          </Button>
        </>
      )}
    >
      <Stack spacing={2}>
        <TextField
          label={t('name', { defaultValue: 'Nombre' })}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(error)}
          helperText={error || ' '}
          fullWidth
          autoFocus
        />
        <TextField
          label={t('contactName', { defaultValue: 'Nombre de contacto' })}
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          fullWidth
        />
        <TextField
          label={t('contactEmail', { defaultValue: 'Correo de contacto' })}
          helperText={t('contactEmailHelp', { defaultValue: 'Se le enviará el código de la organización.' })}
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          type="email"
          fullWidth
        />
        <TextField
          label={t('companyCovered', { defaultValue: 'La empresa cubre' })}
          helperText={t('companyCoveredHelp', { defaultValue: 'Cuánto cubre la empresa por defecto. Precarga los planes que asocies (el miembro paga el resto).' })}
          value={covered}
          onChange={(e) => setCovered(e.target.value)}
          type="number"
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100 } }}
          fullWidth
        />
        <TextField
          label={t('benefits', { defaultValue: 'Beneficios' })}
          helperText={t('benefitsHelp', { defaultValue: "Se muestran a los miembros en 'Mi organización'. Un beneficio por línea." })}
          value={benefits}
          onChange={(e) => setBenefits(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <TextField
          label={t('notes', { defaultValue: 'Notas' })}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
          label={t('activeLabel', { defaultValue: 'Organización activa' })}
        />
      </Stack>
    </PremiumModal>
  );
}

OrganizationForm.propTypes = {
  open: PropTypes.bool.isRequired,
  organization: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    contact_name: PropTypes.string,
    contact_email: PropTypes.string,
    is_active: PropTypes.bool,
    company_covered_percent: PropTypes.number,
    notes: PropTypes.string,
    benefits: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired
};

export default OrganizationForm;
